import HTTP, { baseURL } from './AxiosHTTPClient'
// import { parseISO } from 'date-fns'

import {
  EditMediaItemRequestData,
  Textbook,
  TextbookIssue,
  MediaItemOccurence,
  MediaItem,
  License,
  EditMediaItemOccurenceRequestData,
  User,
  TextbookIssuePublishInfo,
  TextbookIssuePage,
  Volume,
} from '@/shared/types'

let errorHandler = undefined as Function
export const initErrorHandler = (anErrorHandler: Function) => {
  errorHandler = anErrorHandler
}
export class AuthenticationError extends Error {
  params = {}
}
export class ApiError extends Error {
  params = {}
}
export const handleApiError = (response: {
  status: number
  data: { message: string; params: {} }
}) => {
  let error = new ApiError(response.data.message)
  if (response.status === 401) {
    error = new AuthenticationError('authentication error')
  }
  if (response.data.params) {
    error.params = response.data.params
  }
  errorHandler(error)
  throw error
}

export const getFrontendLink = (
  isbn: string,
  isLiveVersion: boolean
): string => {
  if (!process.env.VUE_APP_MEDIATHEK_FRONTEND_ENTRYPOINT) {
    console.error('MEDIATHEK_FRONTEND_ENTRYPOINT not set')
  }
  const entrypoint =
    process.env.VUE_APP_MEDIATHEK_FRONTEND_ENTRYPOINT || 'https://localhost/#'

  const path = isLiveVersion ? `/${isbn}` : `/${isbn}-preview`
  return `${entrypoint}${path}`
}

export const previewLink = (textbookIssue: TextbookIssue): string => {
  const path = `/textbook-issue/${textbookIssue.id}/preview`
  return `${baseURL}${path}`
}

export const previewTextbookIssue = async (
  textbookIssue: TextbookIssue,
  licenseIds: string[]
): Promise<void> => {
  let url = `${baseURL}/textbook-issue/${textbookIssue.id}/preview`
  const urlParams = new URLSearchParams()
  licenseIds.forEach((licenseId: string) => {
    urlParams.append('licenseIds[]', licenseId)
  })
  url = `${url}?${urlParams}`
  const a = document.createElement('a')
  a.href = url
  a.target = '_blank'
  a.click()
}

export const getUsers = async (): Promise<unknown> => {
  const url = '/users'
  try {
    const response = await HTTP.get(url)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const deleteUsers = async (userIds: string[]): Promise<object> => {
  const url = '/users/delete'
  try {
    const formData = new FormData()
    for (const userId of userIds) {
      formData.append('userId', userId)
    }
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editUserPermissions = async (
  userIds: string[],
  textbookIds: string[],
  textbookIssueIds: string[]
): Promise<Array<string>> => {
  const url = '/users/edit-permissions'
  const formData = new FormData()
  for (const userId of userIds) {
    formData.append('userId[]', userId)
  }
  for (const textbookId of textbookIds) {
    formData.append('textbookId[]', textbookId)
  }
  for (const textbookIssueId of textbookIssueIds) {
    formData.append('textbookIssueId[]', textbookIssueId)
  }
  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const createUser = async (
  email: string,
  password: string,
  firstname: string,
  lastname: string,
  role: string,
  textbookIds: string[],
  textbookIssueIds: string[],
  notifyUser: boolean
): Promise<Array<string>> => {
  const url = `/users`
  const formData = new FormData()
  formData.append('email', email)
  formData.append('password', password)
  formData.append('firstname', firstname)
  formData.append('lastname', lastname)
  formData.append('role', role)
  if (notifyUser) {
    formData.append('notifyUser', '1')
  }
  for (const textbookId of textbookIds) {
    formData.append('textbookId[]', textbookId)
  }
  for (const textbookIssueId of textbookIssueIds) {
    formData.append('textbookIssueId[]', textbookIssueId)
  }
  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editUser = async (
  userId: string,
  role: string,
  textbookIds: string[],
  textbookIssueIds: string[]
): Promise<Array<string>> => {
  const url = `/users/${userId}/edit`
  const formData = new FormData()
  formData.append('role', role)
  for (const textbookId of textbookIds) {
    formData.append('textbookId[]', textbookId)
  }
  for (const textbookIssueId of textbookIssueIds) {
    formData.append('textbookIssueId[]', textbookIssueId)
  }
  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const signUpUser = async (
  code: string,
  email: string,
  password: string,
  firstname: string,
  lastname: string
): Promise<object> => {
  const url = '/users/register'
  try {
    const formData = new FormData()
    formData.append('code', code)
    formData.append('email', email)
    formData.append('password', password)
    formData.append('firstname', firstname)
    formData.append('lastname', lastname)
    const response = await HTTP.post(url, formData)
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editUserData = async (
  firstname: string,
  lastname: string
): Promise<object> => {
  const url = '/users/edit'
  try {
    const formData = new FormData()
    formData.append('firstname', firstname)
    formData.append('lastname', lastname)
    const response = await HTTP.post(url, formData)
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const login = async (
  email: string,
  password: string
): Promise<unknown> => {
  const url = '/authentication_token'
  try {
    const response = await HTTP.post(url, {
      email,
      password,
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getMediaItems = async (
  textbookId: string,
  filters: object,
  limit: number,
  page: number,
  sortBy: string,
  sortDesc: boolean
): Promise<{ results: Array<MediaItem>; total: number }> => {
  let url = `/textbook/${textbookId}/media-items`
  const params = new URLSearchParams({
    limit: limit !== null ? `${limit}` : '10',
    page: page !== null ? `${page}` : '1',
    sortBy,
    ...filters,
  })
  if (sortDesc) {
    params.append('sortDesc', '1')
  }
  url += '?' + params.toString()
  try {
    const response = await HTTP.get(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const uploadMediaItems = async (
  files: Array<{ file: File; name: string }>,
  links: Array<{ url: string; name: string }>,
  textbookId: string,
  type: string,
  category: string|null,
  licenses: Array<string>,
  isDownloadable: boolean
): Promise<unknown> => {
  const url = '/media-item/upload'
  const formData = new FormData()
  formData.append('textbookId', textbookId)
  files.forEach((el: { file: File; name: string }): void => {
    formData.append('mediaFiles[]', el.file)
    formData.append('mediaFileNames[]', el.name)
  })
  links.forEach((el: { url: string; name: string }): void => {
    formData.append('links[]', el.url)
    formData.append('linkNames[]', el.name)
  })
  if (type) {
    formData.append('type', type)
  }
  if (category) {
    formData.append('category', category)
  }
  licenses.forEach((el): void => {
    formData.append('licenses[]', el)
  })
  if (isDownloadable) {
    formData.append('isDownloadable', '1')
  }

  try {
    const response = await HTTP.post(url, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const createMediaItemOccurences = async (
  files: Array<string>,
  links: Array<string>,
  textbookIssueId: string,
  pages: number[],
  categoryId: string
): Promise<{ id: string }[]> => {
  const url = `/textbook-issue/${textbookIssueId}/create-media-item-occurences`
  const formData = new FormData()
  formData.append('textbookIssueId', textbookIssueId)
  files.forEach((el: string): void => {
    formData.append('mediaFiles[]', el)
  })
  links.forEach((el: string): void => {
    formData.append('links[]', el)
  })
  pages.forEach((el: number): void => {
    formData.append('pages[]', el.toString())
  })
  formData.append('categoryId', categoryId)

  try {
    const response = await HTTP.post(url, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getTextbookIssue = async (
  textbookIssueId: string
): Promise<unknown> => {
  const url = `/textbook-issue/${textbookIssueId}`

  try {
    const response = await HTTP.get(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getTextbookIssuePage = async (
  textbookIssueId: string,
  page: number
): Promise<Array<MediaItemOccurence>> => {
  const url = `/textbook-issue/${textbookIssueId}/page/${page}`

  try {
    const response = await HTTP.get(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getUserTextbooks = async (
  limit: number | null = null,
  page: number = null
): Promise<Array<Textbook>> => {
  let url = '/textbooks'
  const params = new URLSearchParams({
    limit: limit !== null ? `${limit}` : '10',
    page: page !== null ? `${page}` : '1',
  })
  url += '?' + params.toString()
  try {
    const response = await HTTP.get(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    return data.results
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editTextbook = async (
  id: string,
  name: string
): Promise<Textbook> => {
  const url = `/textbook/${id}`
  try {
    const putData = {
      name,
    }
    const response = await HTTP.put(url, putData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const createTextbook = async (name: string): Promise<Textbook> => {
  const url = `/textbook`
  try {
    const postData = {
      name,
    }
    const response = await HTTP.post(url, postData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const createTextbookIssue = async (
  data: Record<string, string>
): Promise<Array<unknown>> => {
  const url = `/textbook-issue/create`
  try {
    const postData = {
      ...data,
    }
    const formData = new FormData()
    for (const key in postData) {
      const dataValue =
        key === 'news' ? JSON.stringify(postData[key]) : postData[key]
      formData.append(key, dataValue)
    }

    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editTextbookIssue = async (
  data: Record<string, string>
): Promise<TextbookIssue> => {
  const url = `/textbook-issue/edit`
  try {
    const postData = {
      ...data,
    }
    const formData = new FormData()
    for (const key in postData) {
      const dataValue =
        key === 'news' ? JSON.stringify(postData[key]) : postData[key]
      formData.append(key, dataValue)
    }

    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getTextbookIssues = async (
  limit: number | null = null,
  page: number = null
): Promise<Array<unknown>> => {
  let url = '/textbook-issue/all'
  const params = new URLSearchParams({
    limit: limit !== null ? `${limit}` : '10',
    page: page !== null ? `${page}` : '1',
  })
  url += '?' + params.toString()
  try {
    const response = await HTTP.get(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editMediaItem = async (
  mediaItemId: string,
  editMediaItemRequestData: EditMediaItemRequestData
): Promise<Array<unknown>> => {
  const url = `/media-item/${mediaItemId}/edit`
  try {
    const formData = new FormData()
    formData.append('name', editMediaItemRequestData.name ?? '')
    formData.append('description', editMediaItemRequestData.description ?? '')
    formData.append('type', editMediaItemRequestData.type)
    formData.append(
      'isDownloadable',
      editMediaItemRequestData.isDownloadable ? '1' : '0'
    )
    if (editMediaItemRequestData.category) {
      formData.append('category', editMediaItemRequestData.category)
    }
    if (editMediaItemRequestData.licenses) {
      editMediaItemRequestData.licenses.forEach((el): void => {
        formData.append('licenses[]', el)
      })
    }

    const response = await HTTP.post(url, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getCurrentUser = async (): Promise<User> => {
  const url = '/users/auth'

  try {
    const response = await HTTP.get(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const addMediaItemToPage = async (
  textbookIssueId: string,
  mediaItemId: string,
  page: number
): Promise<MediaItemOccurence> => {
  const url = `textbook-issue/${textbookIssueId}/media-item/${mediaItemId}/page/${page}`
  try {
    const response = await HTTP.post(
      url,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const inviteUser = async (
  firstname: string,
  lastname: string,
  email: string,
  role: string
) => {
  const url = `users/invite`
  const formData = new FormData()
  formData.append('firstname', firstname ?? '')
  formData.append('lastname', lastname ?? '')
  formData.append('email', email ?? '')
  formData.append('role', role ?? '')

  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getLicenses = async (): Promise<Array<License>> => {
  const url = `licenses`

  try {
    const response = await HTTP.get(url)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const createLicense = async (
  name: string,
  isTeacherType: boolean
): Promise<unknown> => {
  const url = `licenses/create`
  const formData = new FormData()
  formData.append('name', name)
  formData.append('isTeacherType', isTeacherType ? '1' : '0')

  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editLicense = async (
  id: string,
  name: string,
  isTeacherType: boolean
): Promise<unknown> => {
  const url = `licenses/${id}/edit`
  const formData = new FormData()
  formData.append('name', name)
  formData.append('isTeacherType', isTeacherType ? '1' : '0')

  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const deleteLicenses = async (
  names: Array<string>
): Promise<unknown> => {
  const url = `licenses/delete`
  const formData = new FormData()
  for (const name of names) {
    formData.append('name[]', name)
  }

  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const publishTextbookIssue = async (
  textbookIssueId: string
): Promise<unknown> => {
  const url = `textbook-issue/${textbookIssueId}/publish`

  try {
    const response = await HTTP.post(url, {})

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const createPreviews = async (
  textbookIssueId: string
): Promise<void> => {
  const url = `textbook-issue/${textbookIssueId}/create-previews`

  try {
    const response = await HTTP.post(url, {})

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const publishTextbookIssuePreview = async (
  textbookIssueId: string
): Promise<unknown> => {
  const url = `textbook-issue/${textbookIssueId}/publish-preview`

  try {
    const response = await HTTP.post(url, {})

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const restoreTextbookIssuePublication = async (
  textbookIssueId: string,
  textbookIssuePublicationId: string
): Promise<unknown> => {
  const url = `textbook-issue/${textbookIssueId}/restore`

  try {
    const formData = new FormData()
    formData.append('textbookIssuePublicationId', textbookIssuePublicationId)
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getTextbookIssuePublishInfo = async (
  textbookIssueId: string
): Promise<TextbookIssuePublishInfo> => {
  const url = `textbook-issue/${textbookIssueId}/publish-info`

  try {
    const response = await HTTP.get(url)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const deleteFileVersion = async (
  mediaItemId: string,
  fileVersionId: string,
  confirmed = false
): Promise<unknown> => {
  const url = `media-item/${mediaItemId}/file-version/${fileVersionId}/delete`
  let postData = {}
  if (confirmed) {
    postData = {
      confirmed: '1',
    }
  }
  try {
    const response = await HTTP.post(url, postData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const changeFileVersionStatus = async (
  mediaItemId: string,
  fileVersionId: string,
  status: string
): Promise<unknown> => {
  const url = `media-item/${mediaItemId}/file-version/${fileVersionId}/change-status`
  const formData = new FormData()
  formData.append('status', status)
  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const addCategory = async (
  textbookId: string,
  categoryName: string
): Promise<Textbook> => {
  const url = `textbook/${textbookId}/category`
  const formData = new FormData()
  formData.append('name', categoryName)
  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const logout = async (): Promise<unknown> => {
  const url = `user/logout`

  try {
    const response = await HTTP.get(url)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const reorderMediaItemOccurences = async (
  textbookIssueId: string,
  pageNumber: number,
  order: Array<{
    id: string
    order: string
  }>
): Promise<Array<MediaItemOccurence>> => {
  const url = `textbook-issue/${textbookIssueId}/page/${pageNumber}/reorder`
  const formData = new FormData()
  order.forEach((element: { id: string; order: string }): void => {
    formData.append(`order[${element.id}]`, element.order)
  })
  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const changeLatestFileVersionStatus = async (
  mediaItemIds: string[],
  fileVersionIds: string[],
  status: string
): Promise<Array<MediaItem>> => {
  const url = `media-item/change-latest-file-version-status`
  const formData = new FormData()
  mediaItemIds.forEach((mediaItemId): void => {
    formData.append(`mediaItemIds[]`, mediaItemId)
  })
  fileVersionIds.forEach((fileVersionId): void => {
    formData.append(`fileVersionIds[]`, fileVersionId)
  })
  formData.append('status', status)
  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const changeTextbookIssue = async (
  mediaItemIds: string[],
  textbookIssueId: string
): Promise<Array<MediaItem>> => {
  const url = `media-item/change-textbook-issue/${textbookIssueId}`
  const formData = new FormData()
  mediaItemIds.forEach((mediaItemId): void => {
    formData.append(`mediaItemIds[]`, mediaItemId)
  })

  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const deleteMediaItems = async (
  mediaItemIds: string[]
): Promise<Array<MediaItem>> => {
  const url = `media-item/delete`
  const formData = new FormData()
  mediaItemIds.forEach((mediaItemId): void => {
    formData.append(`mediaItemIds[]`, mediaItemId)
  })

  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getMediaItemOccurences = async (
  mediaItemIds: string[]
): Promise<Array<MediaItemOccurence>> => {
  const params = new URLSearchParams()
  mediaItemIds.forEach((mediaItemId): void => {
    params.append(`mediaItemIds[]`, mediaItemId)
  })

  const url = `media-item/occurences?${params}`

  try {
    const response = await HTTP.get(url)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const getTextbookIssuePages = async (
  textbookIssueId: string,
  limit: number,
  page: number
): Promise<TextbookIssuePage[]> => {
  const params = new URLSearchParams()
  params.append('limit', `${limit}`)
  params.append('page', `${page}`)

  const url = `/textbook-issue/${textbookIssueId}/pages?${params}`

  try {
    const response = await HTTP.get(url)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const mediaItemFileVersionLink = (
  mediaItemId: string,
  fileVersionId: string
): string | null =>
  mediaItemId && fileVersionId
    ? `${baseURL}/media-item/${mediaItemId}/version/${fileVersionId}`
    : null

export const mediaItemFileLink = (mediaItemId: string): string | null =>
  mediaItemId ? `${baseURL}/media-item/${mediaItemId}/file` : null

export const textbookIssuePageLink = (textbookIssueId: string, page: number) =>
  textbookIssueId && page
    ? `${baseURL}/textbook-issue/${textbookIssueId}/view-page/${page}`
    : null

export const textbookIssuePageQrLink = (
  textbookIssueId: string,
  page: number
) =>
  textbookIssueId && page
    ? `${baseURL}/textbook-issue/${textbookIssueId}/qr/${page}`
    : null

export const deleteMediaItemOccurences = async (
  textbookIssueId: string,
  mediaItemOccurenceIds: Array<string>
): Promise<{ ids: Array<string> }> => {
  const url = `textbook-issue/${textbookIssueId}/media-item-occurences/delete`
  const formData = new FormData()
  mediaItemOccurenceIds.forEach((id): void => {
    formData.append(`mediaItemOccurenceIds[]`, id)
  })

  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const forgotPassword = async (
  email: string,
  userCode: string
): Promise<void> => {
  const url = `users/forgot-password`
  const formData = new FormData()
  formData.append(`email`, email)
  formData.append(`userCode`, userCode)

  try {
    await HTTP.post(url, formData)
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const resetPassword = async (
  email: string,
  userCode: string,
  token: string,
  password: string
): Promise<void> => {
  const url = `users/reset-password`
  const formData = new FormData()
  formData.append(`email`, email)
  formData.append(`userCode`, userCode)
  formData.append(`token`, token)
  formData.append(`password`, password)

  try {
    await HTTP.post(url, formData)
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const deleteCategories = async (
  textbookId: string,
  categoryIds: Array<string>
): Promise<Array<{ id: string }>> => {
  const url = `categories/delete`
  const formData = new FormData()
  formData.append(`textbookId`, textbookId)
  categoryIds.forEach((id) => {
    formData.append(`categoryIds[]`, id)
  })

  try {
    const response = await HTTP.post(url, formData)
    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editCategory = async (
  textbookId: string,
  categoryId: string,
  name: string
): Promise<Textbook> => {
  const url = `textbook/${textbookId}/category/${categoryId}/edit`
  const formData = new FormData()
  formData.append('name', name)

  try {
    const response = await HTTP.post(url, formData)
    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const addSelectedMediaItemsToPages = async (
  textbookIssueId: string,
  mediaItemIds: string[],
  pages: number[]
): Promise<Array<MediaItem>> => {
  const url = `textbook-issue/${textbookIssueId}/add-to-pages`
  const formData = new FormData()
  mediaItemIds.forEach((mediaItemId): void => {
    formData.append(`mediaItemIds[]`, mediaItemId)
  })
  pages.forEach((page): void => {
    formData.append(`pages[]`, `${page}`)
  })

  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const addMediaItemOccurences = async (
  textbookIssueId: string,
  mediaItemIds: string[],
  pages: number[],
  deleteOccurences: boolean
): Promise<{
  created: Array<{ id: string }>
  deleted: Array<{ id: string }>
}> => {
  const url = `textbook-issue/${textbookIssueId}/add-media-item-occurences`
  const formData = new FormData()
  mediaItemIds.forEach((mediaItemId): void => {
    formData.append(`mediaItemIds[]`, mediaItemId)
  })
  pages.forEach((page): void => {
    formData.append(`pages[]`, `${page}`)
  })
  if (deleteOccurences) {
    formData.append('deleteOccurences', '1')
  }

  try {
    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const reorderCategories = async (
  textbookId: string,
  order: Array<{
    id: string
    order: string
  }>
): Promise<TextbookIssue> => {
  const url = `textbook/${textbookId}/categories/reorder`
  const formData = new FormData()
  order.forEach((element: { id: string; order: string }): void => {
    formData.append(`order[${element.id}]`, element.order)
  })
  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editMediaItemOccurence = async (
  textbookIssueId: string,
  mediaItemOccurenceId: string,
  data: EditMediaItemOccurenceRequestData
): Promise<MediaItemOccurence> => {
  const url = `textbook-issues/${textbookIssueId}/media-item-occurences/${mediaItemOccurenceId}/edit`
  const formData = new FormData()
  data.licenseIds.forEach((el) => {
    formData.append('licenseIds[]', el)
  })
  if (data.isDownloadable) {
    formData.append('isDownloadable', '1')
  }

  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const importTextbookIssue = async (
  file: string
): Promise<TextbookIssue> => {
  const url = `import-textbook-issue`
  const formData = new FormData()
  formData.append('file[]', file)

  try {
    const response = await HTTP.post(url, formData)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const deleteTextbookIssue = async (
  textbookIssueId: string
): Promise<void> => {
  const url = `textbook-issue/${textbookIssueId}`

  try {
    const response = await HTTP.delete(url)

  } catch ({ response }) {
    handleApiError(response)
  }
}

export const deleteTextbook = async (
  textbookId: string
): Promise<MediaItemOccurence> => {
  const url = `textbook/${textbookId}`

  try {
    const response = await HTTP.delete(url)

    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const changePage = async (
  textbookIssueId: string,
  mediaItemOccurenceIds: Array<string>,
  page: number
): Promise<string[]> => {
  const url = `/textbook-issue/${textbookIssueId}/change-page/${page}`
  const formData = new FormData()
  mediaItemOccurenceIds.forEach((el: string): void => {
    formData.append('mediaItemOccurenceIds[]', el)
  })

  try {
    const response = await HTTP.post(url, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const importMediaFromZip = async (
  textbookId: string,
  zip: string,
  onUploadProgress: (progressEvent: ProgressEvent<EventTarget>) => void
): Promise<string[]> => {
  const url = `/textbook/${textbookId}/zip-media-import`
  const formData = new FormData()
  formData.append('zip', zip)

  try {
    const response = await HTTP.post(url, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
      onUploadProgress,
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const userInfoFromCode = async (code: string): Promise<User> => {
  const url = `users/from-code/${code}`

  try {
    const response = await HTTP.get(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const checkPassword = async (password: string): Promise<void> => {
  const url = `users/check-password`

  const formData = new FormData()
  formData.append('password', password)

  try {
    const response = await HTTP.post(url, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  const url = `users/change-password`

  const formData = new FormData()
  formData.append('currentPassword', currentPassword)
  formData.append('newPassword', newPassword)

  try {
    const response = await HTTP.post(url, formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    return data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const createVolume = async (
  textbookId: string,
  data: Record<string, string>
): Promise<Array<unknown>> => {
  const url = `textbook/${textbookId}/volume`
  try {
    const postData = {
      ...data,
    }
    const formData = new FormData()
    for (const key in postData) {
      const value = postData[key]
      if(Array.isArray(value)) {
        value.forEach(el => {
          formData.append(key + '[]', el)
        })
        continue
      }
      formData.append(key, value)
    }

    const response = await HTTP.post(url, formData)

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const editVolume = async (
  textbookId: string,
  volumeId: string,
  data: Record<string, string|Array<string>>
): Promise<Volume> => {
  const url = `/textbook/${textbookId}/volume/${volumeId}`
  try {
    // const postData = {
    //   ...data,
    // }
    // const formData = new FormData()
    // for (const key in postData) {
    //   const value = postData[key]
    //   if(Array.isArray(value)) {
    //     value.forEach(el => {
    //       formData.append(key + '[]', el)
    //     })
    //     continue
    //   }
      // formData.append(key, value)
    // }

    const response = await HTTP.put(url, data, {headers: {
      'Content-Type': 'application/json',
    }})

    return response.data
  } catch ({ response }) {
    handleApiError(response)
  }
}

export const deleteVolume = async (
  textbookId: string,
  volumeId: string
): Promise<void> => {
  const url = `textbook/${textbookId}/volume/${volumeId}`

  try {
    await HTTP.delete(url)

  } catch ({ response }) {
    handleApiError(response)
  }
}
